import _ from 'lodash';
import React from 'react';
import Debug from 'debug';
import connectToStores from 'fluxible-addons-react/connectToStores';
import { GoogleMap, GoogleMapLoader, Marker } from "react-google-maps";
import Helmet from 'react-helmet';
import Footer from '../Footer';
import Navbar from '../Navbar';
import AppStoreBadges from '../AppStoreBadges';
import shallowCompare from '../../lib/shallowCompare';
import getThumbnailPhotoUrlFromHash from '../../lib/getThumbnailPhotoUrlFromHash';
import CustomerStore from '../../stores/CustomerStore';
import ShoppingCartStore from '../../stores/ShoppingCartStore';
import StoreStore from '../../stores/StoreStore';
import styles from './index.sass';
import addressIcon from './images/about_address_3x.png';
import clockIcon from './images/about_clock_3x.png';
import emailIcon from './images/about_email_3x.png';
import phoneIcon from './images/about_phone_3x.png';

const debug = Debug('delectable');

class StoreAboutPage extends React.Component {
  static contextTypes = {
    executeAction : React.PropTypes.func,
    getUserAgent  : React.PropTypes.func
  };

  static propTypes = {
    currentRoute           : React.PropTypes.object,
    store                  : React.PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = this.getStateFromProps(props);
  }

  getStateFromProps(props) {
    return {
      routeName              : props.currentRoute.name,
      store                  : props.store
    };
  }

  componentWillReceiveProps(nextProps) {
    this.setState(this.getStateFromProps(nextProps));
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  getHelmetProps(store) {
    let description = null;
    let title = `About – ${store.name}`;
    return {
      title: title,
      meta: [
        {name: 'description', content: description},
        {property: 'og:title', content: title},
        {property: 'og:description', content: description},
        {property: 'og:url', content: store.about_url},
        {name: 'twitter:card', content: 'summary_large_image'},
        {name: 'twitter:title', content: title},
        {name: 'twitter:description', content: description}
      ]
    };
  }

  renderMapSection(store) {
    if(!store.hours) {return <br/>;}
    let storeName = store.name;
    let address = store.address;
    if(!address || !address.latitude || !address.longitude) {return <br/>;}
    return (
      <div>
        <p className={styles.mapHeader}>Come visit us</p>
        <GoogleMapLoader
          containerElement={
            <div className={styles.mapContainer} />
          }
          googleMapElement={
            <GoogleMap
              ref={googleMap => {}}
              options={{draggable: false, scrollwheel: false}}
              defaultZoom={14}
              defaultCenter={{lat: address.latitude, lng: address.longitude}}>
              <Marker position={{lat: address.latitude, lng: address.longitude}} key={storeName} />
            </GoogleMap>
          }
        />
      </div>
    );
  }

  renderAddressRow(address) {
    if(!address) {return null;}
    return (
      <div className={styles.row}>
        <span className={styles.iconCell}>
          <img className={styles.addressIcon} src={addressIcon} />
        </span>
        <a className={styles.stringCell} href={address.map_url} target="_blank">
          <span>{address.addr1} {address.addr2}</span>
          <br/>
          <span>{address.city}, {address.state} {address.zip_code}</span>
        </a>
      </div>
    );
  }

  renderHoursRow(hours) {
    if(!hours) {return null;}
    return (
      <div className={styles.row}>
        <span className={styles.iconCell}>
          <img className={styles.clockIcon} src={clockIcon} />
        </span>
        <span className={styles.stringCell} dangerouslySetInnerHTML={{__html: hours.replace(/\n/g, '<br/>')}}></span>
      </div>
    );
  }

  renderEmailRow(contactEmail) {
    if(!contactEmail) {return null;}
    return (
      <div className={styles.row}>
        <span className={styles.iconCell}>
          <img className={styles.emailIcon} src={emailIcon} />
        </span>
        <a className={styles.stringCell} href={`mailto:${contactEmail}`}>{contactEmail}</a>
      </div>
    );
  }

  renderPhoneRow(store) {
    if(!store.phone_pretty) {return null;}
    return (
      <div className={styles.row}>
        <span className={styles.iconCell}>
          <img className={styles.phoneIcon} src={phoneIcon} />
        </span>
        <a className={styles.stringCell} href={`tel:${store.phone_href_format}`}>{store.phone_pretty}</a>
      </div>
    );
  }

  renderDownloadLink(store, hideForWebview) {
    let branchLink = store.referral_link_url;
    if(!branchLink || hideForWebview) {return null;}
    return <AppStoreBadges appStoreLink={branchLink} playStoreLink={branchLink} />;
  }

  renderBrand(store, hideForAndroidWebview) {
    if(hideForAndroidWebview) {return null;}
    return (
      <div className={styles.brand}>
        <div className={styles.avatar} style={{background: `url(${getThumbnailPhotoUrlFromHash(store.avatar_photo_hash, 140)}) no-repeat center center`, backgroundSize: 'cover'}} />
        <h1 className={styles.name} itemProp="name">{store.name}</h1>
        <p className={styles.location}>{store.location}</p>
      </div>
    );
  }

  renderDescription(store, hideForiOSWebview) {
    if(hideForiOSWebview) {return null;}
    return (
      <p className={styles.description} itemProp="description" dangerouslySetInnerHTML={{__html: store.description.replace(/\n/g, '<br/>')}}></p>
    );
  }

  renderHeader(store, hideForWebview) {
    if(hideForWebview) {return null;}
    return <div className={styles.header} style={{background: `url(${getThumbnailPhotoUrlFromHash(store.background_photo_hash, 1280)}) no-repeat center center`, backgroundSize: 'cover'}} />;
  }

  renderNavbar(store, hideForWebview) {
    if(hideForWebview) {return null;}
    let navbarProps = {
      currentRoute: this.props.currentRoute,
      customer: this.props.customer,
      store: this.props.store,
      shoppingCart: this.props.shoppingCart
    };
    return <Navbar {...navbarProps} />;
  }

  renderFooter(store, hideForWebview) {
    if(hideForWebview) {return null;}
    return <Footer store={store} />;
  }

  renderJSONLD(store) {
    let jsonLD = {
      "@context": "http://schema.org",
      "@type": "Organization",
      "email": store.contact_email,
      "telephone": store.phone_pretty,
      "url": store.about_url
    };
    if(store.background_photo_hash) {
      jsonLD.image = store.background_photo_hash.url;
    }
    if(store.logo_photo_hash) {
      jsonLD.logo = store.logo_photo_hash.url;
    }
    let address = store.address;
    if(address) {
      jsonLD.address = {
        "@type": "PostalAddress",
        "addressLocality": `${address.city}, ${address.state}, United States`,
        "postalCode": address.zip_code,
        "streetAddress": `${address.addr1}`
      };
      if(address.addr2) {
        jsonLD.address.streetAddress += ` ${address.addr2}`;
      }
    }
    return <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(jsonLD)}} />;
  }

  render() {
    if(!this.state || !this.state.store) {return null;}
    let store = this.state.store;
    let useragent = this.context.getUserAgent();
    let hideForiOSWebview = false;
    let hideForAndroidWebview = false;
    if(this.props.currentRoute.query.webview === 'true') {
      hideForiOSWebview = useragent.isiOs;
      hideForAndroidWebview = useragent.isBanquetForAndroid;
    }

    return (
      <div className={styles.store} itemScope itemType="http://schema.org/Organization">
        <Helmet {...this.getHelmetProps(store)} />
        {this.renderNavbar(store, hideForiOSWebview || hideForAndroidWebview)}
        {this.renderHeader(store, hideForiOSWebview || hideForAndroidWebview)}
        {this.renderBrand(store, hideForAndroidWebview)}
        {this.renderDownloadLink(store, hideForiOSWebview || hideForAndroidWebview)}
        {this.renderDescription(store, hideForiOSWebview)}
        {this.renderMapSection(store)}
        <div className={styles.aboutCard}>
          {this.renderAddressRow(store.address)}
          {this.renderHoursRow(store.hours)}
          {this.renderEmailRow(store.contact_email)}
          {this.renderPhoneRow(store)}
        </div>
        {this.renderFooter(store, hideForiOSWebview || hideForAndroidWebview)}
        {this.renderJSONLD(store)}
      </div>
    );
  }
}

export default StoreAboutPage;



// WEBPACK FOOTER //
// banquet/components/StoreAboutPage/index.js